/**
 * SdkBackendAdapter — Phase 8.2 of the productionize migration.
 *
 * The long-term goal: one `Backend` interface in `@tento/lona/sheets`,
 * with the SDK supplying adapters that connect it to `Network` routes,
 * sync polling, and a host's code. This class implements the SDK
 * `Backend` interface (`./sync-backend.ts`) by composing two sources:
 *
 * - `sheets: @tento/lona/sheets.Backend` — the destination. Methods
 *   that map cleanly translate Network shapes to/from sheets
 *   `CellEntry` / `RawRow` and delegate.
 * - `legacy: Backend` — the existing SDK backend. Methods that don't
 *   map (architectural reasons, documented per-method below)
 *   keep delegating here.
 *
 * ## Method status (SDK-R1-07 residual list, 2026-10-03)
 *
 * **Migrated to sheets** (5): `writeCell`, `clearCell`, `deleteRow`,
 * `updateRow` — write methods with structurally compatible shapes — and
 * `fetchRowCells`, the keyed read (`fetchCellsByKey`, whose timepoint lane
 * keeps a datetime cell's range; `fetchedAsWireCells` spells it on the wire).
 *
 * **Still on legacy** — what each serves today, and what replaces it:
 *
 * | method | serves (production caller) | replaced by |
 * | --- | --- | --- |
 * | `writeTimepointCell` / `clearTimepointCell` | `CellAccessor.setCustom` / `deleteCustom`, the sheet row's timepoint writes | sheets `writeTimepointCell` once it answers server truth (the response carries the stored cell) |
 * | `addRow` | `RowAccessor` / the client's add path (server-assigned id + position) | sheets `addRow` once it returns the server row |
 * | `rowHandle` / `rekeyRowHandle` / `releaseRowHandles` | every `RowCellCache` consumer; promotion re-key; client dispose | the sheet-scoped stores (`CellStoreView` authority) once no consumer reads a per-row cache |
 * | `rowMeta` / `storeRowMeta` | sheet tree, accessor, bulk install (`Network.SheetRow` metadata) | the rows-side model's `RawRow` metadata |
 * | `applySync` / `syncPollState` | `SyncClient`, bulk install, the client's poll state | the sheets sync watcher (`SyncKeyWatcher`) on the rows side |
 *
 * Closing each gap is its own structural piece — typically requires
 * sheets-side capability additions or consumer rewiring, not a pure
 * adapter shape translation. Phase B step 5 retires the SDK Backend
 * interface entirely once those gaps close.
 */

import type { Option } from "@tento-lona/sheets";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { LiveData } from "@tento-core/livedata";
import {
  type Backend as SheetsBackend,
  type CellEntry,
  CellId,
  type CellValue,
  type Etag,
  type Json,
  type RawRow,
  RowNetworkKey,
  SheetNetworkKey,
  type SyncKey,
} from "@tento-lona/sheets";
import { RowId } from "@tento-lona/sheets";
import { Network } from "../network.ts";
import type { SyncPollEntry } from "./sync-poll-entry.ts";
import type { CellCachingBackend } from "./sync-backend.ts";
import type { CellCardinality } from "@tento-lona/sheets/platform";
import { fetchedAsWireCells, type RowCellCache } from "./row-cell-cache.ts";
import { UtcDays } from "@tento-lona/sheets/internal-sdk";

export class SdkBackendAdapter implements CellCachingBackend {
  readonly #legacy: CellCachingBackend;
  #authority: Option<(row: RowCellCache) => RowCellCache.View> = null;

  /** SDK composition installs a stable client-owned authority before handing out a cache. */
  attachCellAuthority(factory: (row: RowCellCache) => RowCellCache.View): void { this.#authority = factory; }
  readonly #sheets: SheetsBackend;
  constructor(
    sheets: SheetsBackend,
    legacy: CellCachingBackend,
  ) {
    this.#sheets = sheets;
    this.#legacy = legacy;
  }

  // ── Migrated to sheets Backend ────────────────────────────────────
  //
  // These write methods (`writeCell`, `clearCell`, `deleteRow`, and
  // `updateRow` below) translate the SDK's wire-shaped arguments into
  // sheets' typed `CellEntry` / `RawRow` shapes and delegate to
  // `this.sheets`. Behaviour-preserving for the common case (no etag
  // plumbing yet); the legacy backend's `JSON.stringify` workaround
  // for object data is dropped — sheets' `CellEntry.value` is typed
  // `CellValue` and serializes naturally.

  async writeCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    data: Option<Json>,
    expectedEtag: Option<Etag>,
    requestId: Option<string>,
  ): Promise<void> {
    const cell: CellEntry = {
      date,
      id: null,
      value: data ?? null,
      style: null,
    };
    // `null` = no etag known (a wire etag is minted by `Etag.of` where it arrives).
    await this.#sheets.writeCell(rowId, cell, expectedEtag, requestId);
  }

  async clearCell(
    rowId: RowId,
    date: NaiveDate.Partial,
    id: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    await this.#sheets.clearCell(rowId, date, id, expectedEtag);
  }

  async deleteRow(
    sheetKey: SheetNetworkKey,
    rowKey: RowNetworkKey,
    expectedEtag: Option<Etag>,
  ): Promise<void> {
    void sheetKey;
    await this.#sheets.deleteRow(RowId.of(rowKey), expectedEtag);
  }

  // ── Reads — through sheets ────────────────────────────────────────
  //
  // `fetchRowCells` reads the row by key through the sheets `Backend`
  // (`fetchCellsByKey`, the read `RowFetchPlan` uses) over the days the
  // range spans. Its `FetchedCells` keep a point-time cell in the
  // timepoint lane with its full `DateTime` range, so a scheduling slot
  // keeps its time (the reason this read once stayed on legacy, when
  // sheets answered date-anchored `CellEntry`s only).

  async fetchRowCells(
    rowId: RowId,
    range: DateTime.Range<Utc>,
  ): Promise<Network.SheetCell[]> {
    // The days the range touches (09:00-17:00 on D reads D..D+1).
    const fetched = await this.#sheets.fetchCellsByKey(RowNetworkKey.forRead(rowId), UtcDays.touched(range));
    return fetchedAsWireCells(fetched);
  }

  // ── Architectural — stay on legacy ────────────────────────────────
  //
  // These methods don't have clean sheets-Backend equivalents;
  // each is documented below with the specific gap that prevents
  // migration. They pass through to `legacy` unchanged. Closing
  // each gap is its own structural piece of work.

  /**
   * **Stays on legacy.** Custom-cell write returns
   * `Network.CellWriteResponse` carrying server-computed
   * `affectedStart` / `affectedEnd` / canonical cells — used by
   * `sheet-cell-accessor.ts:setCustom` to remove the replaced cell id
   * and upsert server-returned custom cells.
   *
   * Sheets' `writeCell` takes a `CellEntry` (date-anchored, not
   * datetime) and returns void. Migrating requires either
   * extending sheets' `Backend` to accept `CellEntry.Timepoint`
   * inputs and surface custom-write output, or moving the
   * server-truth logic client-side. Both are sheets-side
   * commitments. Leave on legacy until the sheets seam grows
   * datetime cell support.
   */
  writeTimepointCell(
    rowId: RowId,
    dateRange: DateTime.Range<Utc>,
    data: Option<Json | CellValue>,
    replaceCellId: Option<CellId>,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse> {
    return this.#legacy.writeTimepointCell(
      rowId,
      dateRange,
      data,
      replaceCellId,
      expectedEtag,
    );
  }

  /** Stays on legacy — same seam as `writeTimepointCell`. */
  clearTimepointCell(
    rowId: RowId,
    cellId: CellId,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CellWriteResponse> {
    return this.#legacy.clearTimepointCell(rowId, cellId, expectedEtag);
  }

  /**
   * **Stays on legacy.** `RowCellCache` is the SDK's per-row class
   * holding the fetched cell cache, `encoding`, sync state,
   * `applyMeta` derivation, etc. Sheets' equivalent state lives
   * in per-sheet stores (`sourceCellData`, `rowMetadata`) keyed
   * by `RowLocalId`, not in a per-row instance class.
   *
   * Migrating requires consumers to drop the SDK's per-row
   * state-bearing class in favor of sheets' store-backed lookups.
   * That's the centerpiece of Phase B step 5: "RowCellCache retires
   * last when nothing imports it." Leave on legacy until indices
   * (Phase C) finish migrating off `RowCellCache`-based factories.
   */
  rowHandle(rowId: RowId): RowCellCache {
    const cache = this.#legacy.rowHandle(rowId);
    if (cache.view == null && this.#authority != null) cache.setView(this.#authority(cache));
    return cache;
  }

  /** The row's cache if one exists, building none (`CellCachingBackend.peekRowHandle`). */
  peekRowHandle(rowId: RowId): Option<RowCellCache> {
    return this.#legacy.peekRowHandle(rowId);
  }

  /** Promote re-key: delegate to the legacy backend's handle registry. */
  rekeyRowHandle(from: RowId, to: RowId): void {
    this.#legacy.rekeyRowHandle(from, to);
  }

  /** Client dispose: release the legacy backend's handle registry. */
  releaseRowHandles(): void {
    this.#legacy.releaseRowHandles();
  }

  /** The sheet-wide bulk read: the sheets backend's. */
  sheetReads(): Pick<SheetsBackend, "fetchSheetCells"> {
    return { fetchSheetCells: (sheetKey, range) => this.#sheets.fetchSheetCells(sheetKey, range) };
  }

  /**
   * **Stays on legacy.** SDK's `addRow` returns a fully-populated
   * `Network.CreateRowResponse` (a `SheetRow` with the
   * server-assigned `id` + `position` + `ownerId`). The legacy
   * server is the authority for those fields.
   *
   * Sheets' `addRow` accepts a `RawRow` with `id` already set
   * and returns void. Migrating means generating ids and
   * positions client-side AND synthesizing the response shape —
   * doable, but requires a `RawRow ↔ Network.SheetRow`
   * translation surface (different field sets — RawRow lacks
   * `ownerId`/`position`, Network.SheetRow has them) plus a
   * client-side id authority. Defer until consumers can drop the
   * server-assigned-id assumption (currently only test-only
   * production usage per the audit, so this is a pure
   * future-cleanup item).
   */
  addRow(
    sheetKey: SheetNetworkKey,
    attrs: Network.NewRowBody,
    expectedEtag: Option<Etag>,
  ): Promise<Network.CreateRowResponse> {
    return this.#legacy.addRow(sheetKey, attrs, expectedEtag);
  }

  async updateRow(
    rowKey: RowNetworkKey,
    patch: Network.PatchRow_PendingValidation,
    expectedEtag: Option<Etag>,
  ): Promise<Network.PatchRowResponse> {
    // Migrated to sheets: `Backend.updateRow` takes the row PATCH body
    // (`Backend.UpdateRowBody`, this route's own shape) and answers the server's
    // `shouldRefetchData` hint.
    const answer = await this.#sheets.updateRow(RowId.of(rowKey), patch, expectedEtag);
    return { shouldRefetchData: answer.shouldRefetchData };
  }

  /**
   * **Stays on legacy.** `rowMeta` and `storeRowMeta` form the SDK's
   * per-Client metadata cache — `Map<rowKey, Network.SheetRow>`.
   * Sheets' equivalent is `Sheet.rowMeta` (a per-sheet
   * `RowMetadataStore`), which is keyed by `RowLocalId` and uses
   * `RawRow`/typed fields, not the wire `Network.SheetRow` shape.
   *
   * Migration requires either translating Network.SheetRow ↔
   * RowMetadata at the boundary (and accepting the per-sheet
   * scoping) or rewriting the SDK's row-meta consumers to work
   * with the per-sheet store. Leave on legacy until the broader
   * RowCellCache retirement (Phase B step 5) makes the SDK-side
   * cache obsolete.
   */
  rowMeta(rowId: RowId): Option<Network.SheetRow> {
    return this.#legacy.rowMeta(rowId);
  }

  storeRowMeta(rowId: RowId, row: Network.SheetRow, typeCardinality: CellCardinality): void {
    this.#legacy.storeRowMeta(rowId, row, typeCardinality);
  }

  /**
   * **Stays on legacy.** Sync polling — issues `GET /api/sync/{key}`
   * with exponential backoff for HTTP-bound sync jobs (e.g.
   * `:~google:…:events` provider syncs). Sheets has no
   * equivalent: in a CRDT or local-first model, sync becomes
   * eventual-consistency merges, not server-job polling, and the
   * concept folds away.
   *
   * Stays on legacy until either (a) sync polling is removed from
   * the consumer surface (sheet-cell-accessor) or (b) the sheets
   * Backend grows its own job-status mechanism. The latter would
   * be a sheets-architectural change; the former is consumer-
   * level work.
   */
  applySync(
    rh: RowCellCache,
    syncKeys: readonly SyncKey[],
    range: DateTime.Range<Utc>,
  ): void {
    this.#legacy.applySync(rh, syncKeys, range);
  }

  /**
   * **Stays on legacy.** Observability hook for sync polling state;
   * read by a host's debug-flags controls. Folds away
   * with `applySync` once the polling concept retires.
   */
  get syncPollState(): Option<
    LiveData.Readable<ReadonlyMap<SyncKey, SyncPollEntry>>
  > {
    return this.#legacy.syncPollState;
  }
}

